import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  EDIT_STEPS,
  PROFILE_GUIDES,
  PROFILE_GUIDE_AUTO_UNTIL,
  PROFILE_GUIDE_STEPS,
  TOUR_TARGETS,
} from './profileGuideConfig';
import useSeenGuide from './useSeenGuide';

const visible = (el) => !!el && el.getClientRects().length > 0;
const find = (selector) => {
  const el = document.querySelector(selector);
  return visible(el) ? el : null;
};

// Any open modal or drawer: the guide waits for it to close.
const overlayOpen = () =>
  [...document.querySelectorAll('.ant-modal-wrap, .ant-drawer-open')].some(
    visible
  );

// Time for the panel to switch into edit mode before the step points at it.
const EDIT_SWITCH_MS = 350;

/** Steps whose element is on the page right now. */
const presentSteps = () =>
  PROFILE_GUIDE_STEPS.filter((id) => {
    // Saving and unsaved changes are only explained to somebody who can edit
    // the open tile.
    if (EDIT_STEPS.includes(id)) {
      return !!find(TOUR_TARGETS.save) || !!find(TOUR_TARGETS.edit);
    }
    return !!find(TOUR_TARGETS[id]);
  });

/**
 * Guide to the profile layout. Opens by itself the first time a person opens
 * this kind of profile (until PROFILE_GUIDE_AUTO_UNTIL, when set); `start`
 * opens it again at any time. For the Save steps it puts the open tile into edit mode and
 * takes it out again when it is done with them.
 *
 * @param {Object} opts
 * @param {'employee'|'company'} opts.kind
 * @param {boolean} opts.ready - the profile has rendered its tiles and panel
 * @param {boolean} opts.blocked - e.g. editing with unsaved changes
 * @param {boolean} opts.isEditing
 * @param {() => void} opts.startEditing
 * @param {() => void} opts.stopEditing
 */
export default function useProfileGuide({
  kind,
  ready,
  blocked,
  isEditing,
  startEditing,
  stopEditing,
}) {
  const { known, seen, markSeen } = useSeenGuide(PROFILE_GUIDES[kind]);
  const [steps, setSteps] = useState(null);
  const [current, setCurrent] = useState(0);
  const autoDone = useRef(false);
  // Edit mode the guide opened itself (and so has to close).
  const ownEdit = useRef(false);
  const switchTimer = useRef(null);

  const start = useCallback(() => {
    const ids = presentSteps();
    // One step is not a guide.
    if (ids.length < 2) return;
    ownEdit.current = false;
    setCurrent(0);
    setSteps(ids);
  }, []);

  const autoAllowed =
    (!PROFILE_GUIDE_AUTO_UNTIL ||
      !dayjs().isAfter(dayjs(PROFILE_GUIDE_AUTO_UNTIL), 'day')) &&
    known &&
    !seen &&
    ready &&
    !blocked;

  useEffect(() => {
    if (!autoAllowed || autoDone.current) return undefined;
    let timer;
    const attempt = (left) => {
      // Let the page settle (tiles, Edit appear after data) and wait out a
      // modal the person may have open.
      if (overlayOpen()) {
        if (left > 0) timer = setTimeout(() => attempt(left - 1), 1000);
        return;
      }
      autoDone.current = true;
      start();
    };
    timer = setTimeout(() => attempt(60), 800);
    return () => clearTimeout(timer);
  }, [autoAllowed, start]);

  useEffect(() => () => clearTimeout(switchTimer.current), []);

  const change = useCallback(
    (next) => {
      clearTimeout(switchTimer.current);
      const wantEdit = EDIT_STEPS.includes(steps?.[next]);
      if (wantEdit && !isEditing) {
        ownEdit.current = true;
        startEditing();
      } else if (!wantEdit && ownEdit.current && isEditing) {
        ownEdit.current = false;
        stopEditing();
      } else {
        setCurrent(next);
        return;
      }
      // Point at the panel once it has switched.
      switchTimer.current = setTimeout(() => setCurrent(next), EDIT_SWITCH_MS);
    },
    [steps, isEditing, startEditing, stopEditing]
  );

  const close = useCallback(() => {
    clearTimeout(switchTimer.current);
    if (ownEdit.current) {
      ownEdit.current = false;
      stopEditing();
    }
    setSteps(null);
    markSeen();
  }, [markSeen, stopEditing]);

  return { steps, current, change, start, close };
}
